import {
  useState,
  useEffect,
  type FC,
  useCallback,
  useRef,
  type ReactNode,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { routes } from "../app/routes";
import { HomeIcon } from "./SvgIcons/HomeIcon";
import {
  AnalyticsActionType,
  type PageViewProps,
} from "../app/contexts/Analytics/types";
import { useShouldSendPageView } from "../app/contexts/Analytics/helpers";
import { getPageNameFromPathName } from "../helpers/urlHelpers";
import {
  ReferrerContext,
  initialReferrerContextState,
} from "../app/contexts/ReferrerContext/context";
import URI from "urijs";
import { parseSearchParams } from "../helpers/objectUtilities";
import { useCurrentPlanYear } from "../helpers/yearFlagHelpers";
import { routesWithQueryParamPages } from "../app/contexts/Analytics/constants";
import { ArrowIcon } from "@cmsgov/ds-medicare-gov";
import {
  ReferrerContextValueKey,
  type ReferrerContextValues,
  RouteReferrerIconKey,
} from "../app/contexts/ReferrerContext/types";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { usePrevious } from "../helpers/usePrevious";
import { useDebounceCallback } from "usehooks-ts";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- JSDoc reference
import { sendPageView } from "../app/contexts/Analytics/helpers";

/**
 * Map back link icon keys to icons
 * The default icon (`BACK`) is imported directly into `ActionsBar`
 */
export const routeReferrerIconMappings: Record<
  RouteReferrerIconKey,
  JSX.Element
> = {
  [RouteReferrerIconKey.Back]: <ArrowIcon direction="left" />,
  [RouteReferrerIconKey.Home]: <HomeIcon />,
};

/**
 * The routes added to the context mapping
 */
export interface ReferrerRoutes
  extends
    Pick<typeof routes, "searchResults" | "comparePlans" | "yearOverYear">,
    Pick<typeof routes.summary, "landingPage"> {}
export type ReferrerRouteValues = ReferrerRoutes[keyof ReferrerRoutes];

/**
 * Defines what text and optional icon (other than `BACK` / `ArrowIcon`) should
 * be used in the `ActionsBar` to link back to the referring route's pathname.
 * The `default` is a fallback option.
 */
export const routeReferrerContextMappings: {
  [key in ReferrerRouteValues]: Partial<ReferrerContextValues>;
} & { default: Partial<ReferrerContextValues> } = {
  [routes.searchResults]: {
    goBackTextId: "app.terms.go_back_to_search_results",
  },
  [routes.comparePlans]: {
    goBackTextId: "app.terms.go_back_to_plan_compare",
  },
  [routes.yearOverYear]: {
    goBackTextId: "app.terms.go_back_to_year_over_year",
  },
  [routes.summary.landingPage]: {
    goBackTextId: "summary_page.title",
    iconKey: RouteReferrerIconKey.Home,
  },
  default: {
    goBackTextId: "app.terms.start_a_new_search",
  },
};

const isReferrerRoute = (key: string): key is ReferrerRouteValues =>
  Object.keys(routeReferrerContextMappings)
    .filter(key => key !== "default")
    .includes(key);

export const getPageName = ({
  pathname,
  queryParamPage,
}: {
  pathname: string;
  queryParamPage?: string | undefined;
}) => {
  const useQueryParamPage =
    routesWithQueryParamPages.includes(pathname) && queryParamPage;
  return `${getPageNameFromPathName(pathname)}${
    useQueryParamPage ? `/${queryParamPage.toLowerCase()}` : ""
  }`;
};

export const ReferrerDetector: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  // Context
  const {
    state: { tealiumLoaded },
    dispatch,
  } = useAppContext();

  // Hooks
  const location = useLocation();
  const shouldSendPageView = useShouldSendPageView();
  const mctCurrentPlanYear = useCurrentPlanYear();

  // Derived
  const { search } = location;
  const uri = new URI(search);
  const {
    lang,
    plan_type: planType,
    year,
  } = parseSearchParams(uri.search(true));
  const previousLang = usePrevious(lang);
  const previousYear = usePrevious(year);
  const previousPlanType = usePrevious(planType);

  /**
   * Track any query params, other than `page` (see `useQueryParamPage` and related
   * code elsewhere in this component) that should trigger a page view when they change.
   * We're doing this to ensure values not sent with other events (e.g., `buttonEngagement`)
   * register the latest, correct value, given that they might not have been updated
   * since the last `pageview` triggered by a route change.
   *
   * Depending on the value, it may be best to assert that both the previous and
   * current value are defined (i.e., `lang`), since those are captured in the initial
   * `pageview` when someone lands on MCT. For others, you may want to trigger a
   * `pageview` when they're first set, if they may need updating before the next
   * route-change event.
   *
   * Some values aren't sent but affect other values, e.g., `year` is factored into
   * what's sent for `lis`, and may be changed while viewing a single route.
   *
   * @see {@link sendPageView} to know what's being sent with a pageview
   */
  const nonPageQueryParamsChanged = useMemo(
    () =>
      (lang && previousLang && lang !== previousLang) ||
      (planType && planType !== previousPlanType) ||
      (year && previousYear && year !== previousYear),
    [lang, planType, year, previousLang, previousPlanType, previousYear]
  );

  /**
   * Debounce dispatch for sending a `pageview` when various query parameters change.
   * This prevents excessive `pageview`s from being sent. The default debounce
   * delay of 500ms seems fine
   */
  const debouncedDispatch = useDebounceCallback(dispatch);

  // State
  const [previousLocation, setPreviousLocation] = useState("");
  const [previousPage, setPreviousPage] = useState<string | undefined>("");
  const [contextValues, setContextValues] = useState(
    initialReferrerContextState
  );

  // Refs
  const previousLocationRef = useRef<string>("");

  const updateContextValues = useCallback(
    (values: Partial<ReferrerContextValues>) =>
      setContextValues(contextValues => ({ ...contextValues, ...values })),
    []
  );

  /**
   * Update contextValues (for knowing where as user has come from)
   * Send pageview events for route changes and changes to the `page` query
   * parameter on routes where changes to that value should be considered `pageview`s
   */
  useEffect(() => {
    const { pathname, search } = location;
    const routeShouldUseQueryParamPage =
      routesWithQueryParamPages.includes(pathname);
    if (routeShouldUseQueryParamPage && !search) {
      // Wait until query params are populated to determine what value should
      // be used to record a pageview, based on the value of "page" in query params
      return;
    }
    const uri = new URI(search);
    const { page: queryParamPage } = parseSearchParams(uri.search(true));
    const queryParamPageHasChanged = queryParamPage !== previousPage;
    const pageHasChanged = previousLocation !== pathname;
    let useQueryParamPage = false;

    if (pageHasChanged) {
      previousLocationRef.current = previousLocation;
      setPreviousLocation(pathname);
    }

    if (queryParamPageHasChanged) {
      setPreviousPage(queryParamPage);
      if (queryParamPage && routeShouldUseQueryParamPage) {
        useQueryParamPage = true;
      }
    }

    if (pageHasChanged && !useQueryParamPage) {
      updateContextValues({
        ...(isReferrerRoute(previousLocation)
          ? routeReferrerContextMappings[previousLocation]
          : routeReferrerContextMappings.default),
        [ReferrerContextValueKey.PreviousRoute]: previousLocationRef.current,
      });
    }

    /**
     * @TODO - For this specific page-view dispatch, determine whether the
     * `tealiumLoaded` check belongs here or should be handled elsewhere.
     * @see {@link TealiumHandler}
     * @see {@link sendPageView}, which checks for the existence of `utag.view`
     * Consider whether the component tree rooted at `ReferrerDetector` should
     * wait to render until Tealium has either loaded or definitively failed,
     * so that page views from this block are always sent correctly when Tealium works.
     */
    if (
      (pageHasChanged || useQueryParamPage) &&
      shouldSendPageView &&
      tealiumLoaded
    ) {
      let settings: PageViewProps = {
        location,
        mctCurrentPlanYear,
        page_name: getPageName({ pathname }),
      };
      if (useQueryParamPage) {
        settings = {
          ...settings,
          custom_page_name: queryParamPage,
          page_name: getPageName({ pathname, queryParamPage }),
        };
      }
      dispatch({
        type: AnalyticsActionType.SEND_PAGE_VIEW,
        settings,
      });
    }
  }, [
    dispatch,
    location,
    mctCurrentPlanYear,
    previousLocation,
    previousPage,
    shouldSendPageView,
    tealiumLoaded,
    updateContextValues,
  ]);

  /**
   * Watch for changes to `fips` and `plan_type` query parameters and send `pageview`s
   * for those changes, as needed
   *
   * @TODO - determine whether the `tealiumLoaded` check is necessary
   * @see {@link TealiumHandler}
   * @see {@link sendPageView}, which checks for the existence of `utag.view`
   * It's possible that the entire tree from `ReferrerDetector` down also shouldn't
   * render until Tealium is loaded or totally fails to load, since that would
   * guarantee page views should always be sent correctly if Tealium is working
   */
  useEffect(() => {
    if (!tealiumLoaded || !nonPageQueryParamsChanged) {
      return;
    }
    const { pathname } = location;
    debouncedDispatch({
      type: AnalyticsActionType.SEND_PAGE_VIEW,
      settings: {
        page_name: getPageName({ pathname }),
        location,
        mctCurrentPlanYear,
      },
    });
  }, [
    debouncedDispatch,
    location,
    mctCurrentPlanYear,
    nonPageQueryParamsChanged,
    tealiumLoaded,
  ]);

  return (
    <ReferrerContext.Provider
      value={{
        goBackTextId: contextValues.goBackTextId,
        iconKey: contextValues.iconKey,
        previousRoute: contextValues.previousRoute,
      }}
    >
      {children}
    </ReferrerContext.Provider>
  );
};
